/**
 * @file use-stock-filters.ts
 * @description Состояние фильтров экрана «Остатки / Движения» в URL
 * (phase19.24 этап 1, шаг 2).
 *
 * Все значения живут в адресе, а не в `useState`: отобранный журнал шлют
 * коллеге ссылкой, «Назад» из карточки документа обязано вернуть тот же отбор,
 * а обновление страницы не должно сбрасывать период и склад.
 *
 * Значения по умолчанию в URL не пишем — ссылка короткая, а «Сбросить» просто
 * убирает параметры. Соответствие «поле фильтра → параметр» задано реестром
 * `PARAM`: новый фильтр = запись здесь и поле в `StockFilters`.
 */

import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

import { SELECT_PARAM } from "./use-stock-selection";
import { parseStockView, type StockView } from "../stock-view";
import {
  MOVEMENTS_PERIOD_DEFAULT,
  parseMovementsPeriod,
  type MovementsPeriod,
} from "../../../lib/movements-period";
import {
  parseMovementOperation,
  type MovementOperation,
} from "../../../lib/movements-texts";

/** Полный набор фильтров обеих вкладок экрана. */
export interface StockFilters {
  /** Вкладка: остатки или журнал движений. */
  view: StockView;
  /** Поиск по товару, артикулу, номеру партии и документа (клиентский). */
  search: string;
  warehouseId: string;
  /** Вкладка «Движения»: вид операции. */
  operation: MovementOperation;
  period: MovementsPeriod;
  /** Свой период: «с» и «по» в формате `<input type="date">`. */
  from: string;
  to: string;
  /** Вкладка «Остатки»: только позиции с остатком. */
  onlyPositive: boolean;
  /** Вкладка «Остатки»: только склады, доступные для продажи. */
  onlySellable: boolean;
  /**
   * Старый параметр «Партия» (`lot=`). С phase19.31 (SC59) отдельного поля
   * нет: значение уже подставлено в `search`, здесь оно только для «Сбросить»
   * и совместимости ссылок.
   */
  lot: string;
  /** «Остатки»: остаток ниже минимального (серверный `belowMin`; дашборд). */
  belowMin: boolean;
  /** «Остатки»: партии без адреса хранения (серверный `unplaced`). */
  unplaced: boolean;
  /**
   * phase19.22 (W17-9c) — «Без этикеток» (`none`) / «Этикетки устарели»
   * (`stale`), серверный `labelStatus`. Пусто — отбора нет.
   */
  labels: StockLabelsFilter;
  /**
   * «Остатки»: одна фасовка (серверный `skuId`). Поля в строке фильтров нет —
   * параметр приходит ссылкой из «Требует внимания», снимается «Сбросить».
   */
  skuId: string;
}

/** Отбор по этикеткам партии: нет / устарели / без отбора. */
export type StockLabelsFilter = "" | "none" | "stale";

/** Значение параметра `labels` из адреса; мусор — «без отбора». */
function parseLabelsFilter(raw: string | null): StockLabelsFilter {
  return raw === "none" || raw === "stale" ? raw : "";
}

/** Имена параметров в адресе — короткие, ссылка остаётся читаемой. */
const PARAM = {
  view: "view",
  search: "q",
  warehouseId: "wh",
  operation: "op",
  period: "period",
  from: "from",
  to: "to",
  onlyPositive: "positive",
  onlySellable: "sellable",
  lot: "lot",
  // Длинные имена: параметр приходит ссылкой с дашборда, и в адресе он должен
  // читаться человеком без словаря.
  belowMin: "belowMin",
  unplaced: "unplaced",
  labels: "labels",
  skuId: "skuId",
} as const satisfies Record<keyof StockFilters, string>;

/** Значения по умолчанию: их в URL не пишем и по ним считаем «фильтры чистые». */
export const STOCK_FILTERS_DEFAULT: StockFilters = {
  view: "stock",
  search: "",
  warehouseId: "",
  operation: "all",
  period: MOVEMENTS_PERIOD_DEFAULT,
  from: "",
  to: "",
  // Нулевые строки в реестре остатков — шум (TD-58), поэтому «только с
  // остатком» включено по умолчанию.
  onlyPositive: true,
  onlySellable: false,
  lot: "",
  belowMin: false,
  unplaced: false,
  labels: "",
  skuId: "",
};

/**
 * Какие фильтры относятся к вкладке.
 *
 * От этого зависят «Сбросить» и признак «отбор задан»: период операции не
 * должен зажигать кнопку сброса на вкладке остатков. Поиск и склад общие —
 * нашёл товар в остатках, переключился в движения и видишь его же.
 */
const VIEW_FIELDS: Record<StockView, (keyof StockFilters)[]> = {
  stock: [
    "search",
    "warehouseId",
    "onlyPositive",
    "onlySellable",
    "lot",
    "belowMin",
    "unplaced",
    "labels",
    "skuId",
  ],
  // «Партия» общая для вкладок: ссылка «Движения по партии» из «⋯» строки
  // остатка приходит с этим параметром, и «Сбросить» обязано его убирать.
  movements: ["search", "warehouseId", "operation", "period", "from", "to", "lot"],
};

interface StockFiltersApi {
  filters: StockFilters;
  /** Частичное изменение: `setFilters({ period: "7" })`. */
  setFilters: (patch: Partial<StockFilters>) => void;
  /** Вернуть фильтры вкладки к значениям по умолчанию. */
  reset: () => void;
  /** Есть что сбрасывать — кнопка «Сбросить» появляется только тогда. */
  dirty: boolean;
}

/** Записать одно значение в параметры, убрав его при значении по умолчанию. */
function writeParam(
  params: URLSearchParams,
  key: keyof StockFilters,
  value: string | boolean,
): void {
  const fallback = STOCK_FILTERS_DEFAULT[key];
  if (value === fallback || value === "") {
    params.delete(PARAM[key]);
    return;
  }
  params.set(PARAM[key], typeof value === "boolean" ? (value ? "1" : "0") : value);
}

export function useStockFilters(): StockFiltersApi {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<StockFilters>(
    () => ({
      view: parseStockView(searchParams.get(PARAM.view)),
      // SC59: поле «Партия» слилось с поиском. Старые ссылки «Движения по
      // партии» и закладки несут `lot=` — подставляем его в поиск.
      search: searchParams.get(PARAM.search) || searchParams.get(PARAM.lot) || "",
      warehouseId: searchParams.get(PARAM.warehouseId) ?? "",
      operation: parseMovementOperation(searchParams.get(PARAM.operation)),
      period: parseMovementsPeriod(searchParams.get(PARAM.period)),
      from: searchParams.get(PARAM.from) ?? "",
      to: searchParams.get(PARAM.to) ?? "",
      // «Только с остатком» выключается явным `positive=0`: отсутствие
      // параметра — значение по умолчанию, то есть включено.
      onlyPositive: searchParams.get(PARAM.onlyPositive) !== "0",
      onlySellable: searchParams.get(PARAM.onlySellable) === "1",
      lot: searchParams.get(PARAM.lot) ?? "",
      belowMin: searchParams.get(PARAM.belowMin) === "1",
      unplaced: searchParams.get(PARAM.unplaced) === "1",
      labels: parseLabelsFilter(searchParams.get(PARAM.labels)),
      skuId: searchParams.get(PARAM.skuId) ?? "",
    }),
    [searchParams],
  );

  const setFilters = useCallback(
    (patch: Partial<StockFilters>) => {
      const params = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined) continue;
        writeParam(params, key as keyof StockFilters, value);
      }
      // Правка поиска поглощает старый `lot=`: иначе стёртый поиск снова
      // заполнился бы номером партии из адреса.
      if (patch.search !== undefined) params.delete(PARAM.lot);
      // replace: перебор фильтров не должен копиться в истории — «Назад»
      // обязано уводить со страницы, а не отматывать десять правок поиска.
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const reset = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    for (const key of VIEW_FIELDS[parseStockView(searchParams.get(PARAM.view))]) {
      params.delete(PARAM[key]);
    }
    // `select=all` — спутник отборов «Без места» / «Без этикеток»: оставить его после «Сбросить»
    // значит снова отметить весь список следующей же загрузкой.
    params.delete(SELECT_PARAM);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const dirty = useMemo(
    () =>
      VIEW_FIELDS[filters.view].some(
        (key) => filters[key] !== STOCK_FILTERS_DEFAULT[key],
      ),
    [filters],
  );

  return { filters, setFilters, reset, dirty };
}
